import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Box } from "@mui/material";
import FullScreenImageDialog from "../../../shared/components/FullScreenImageDialog";
import useTranslation from "../../../shared/hooks/useTranslation";
import ImageBox from "../../../shared/components/ImageBox";
import TelephoneReceiverIcon from "../../../shared/assets/icons/TelephoneReceiverIcon";
import Tooltip from "../../../shared/components/Tooltip";
import colors from "../../../shared/themes/colors";
import LetterIcon from "../../../shared/assets/icons/LetterIcon";
import WebsiteIcon from "../../../shared/assets/icons/WebsiteIcon";
import { Customer } from "../../../public/models/customersModels";
import { dt } from "../../../shared/themes/redesignTokens";

export interface CustomerProps {
  customer?: Customer;
}

const CustomerCard = ({ customer }: CustomerProps) => {
  const [copyField, setCopyField] = useState<string>();
  const [isFullScreenImage, setOpenFullScreenImage] = useState(false);
  const { translate: tCommon } = useTranslation("common");

  const onCopy = (value: string | undefined, copyField: string) => {
    if (value && navigator.clipboard) {
      navigator.clipboard.writeText(value);
      setCopyField(copyField);
    }
  };

  const contactHover = {
    ":hover": {
      color: dt.blue
    }
  };

  if (!customer) {
    return <></>;
  }

  return (
    <>
      <Box
        className="flex flex-col self-start p-4"
        sx={{
          background: colors.primary.white1,
          border: `1px solid ${colors.secondary.gray94}`,
          borderRadius: "16px",
          width: "100%"
        }}
      >
        <Box className="flex flex-col text-center items-center">
          <Box className="cursor-pointer" onClick={() => setOpenFullScreenImage(true)}>
            <ImageBox src={customer?.logo?.content} customSize={64} />
          </Box>
          <Box className="flex flex-col items-center mt-2">
            <Box className="text-16-bold" color="#333333">
              {customer?.name}
            </Box>
          </Box>
        </Box>

        <Box className="block m-auto">
          <Box className="text-16-regular" color={"#333333"}>
            <Box className="flex gap-2 mt-2 w-full justify-start">
              <TelephoneReceiverIcon />
              <Tooltip
                title={
                  copyField === "phone" ? (
                    <Box className="text-12-bold" color={colors.primary.white1}>
                      {tCommon("copied")}
                    </Box>
                  ) : (
                    <Box className="text-12-bold" color={colors.primary.white1}>
                      {tCommon("copy")}
                    </Box>
                  )
                }
                placement="top"
                arrow
              >
                <Box
                  component="a"
                  href={`tel: ${customer?.phone}`}
                  sx={contactHover}
                  className="cursor-pointer"
                  onClick={() => onCopy(customer?.phone, "phone")}
                >
                  {customer?.phone}
                </Box>
              </Tooltip>
            </Box>
          </Box>

          <Box className="text-16-regular" color={"#333333"}>
            <Box className="flex gap-2 mt-2 w-full justify-start">
              <LetterIcon />
              <Box component="a" href={`mailto: ${customer?.email}`}>
                <Tooltip
                  title={
                    copyField === "email" ? (
                      <Box className="text-12-bold" color={colors.primary.white1}>
                        {tCommon("copied")}
                      </Box>
                    ) : (
                      <Box className="text-12-bold" color={colors.primary.white1}>
                        {tCommon("copy")}
                      </Box>
                    )
                  }
                  placement="top"
                  arrow
                >
                  <Box
                    sx={contactHover}
                    className="cursor-pointer break-all"
                    onClick={() => onCopy(customer?.email, "email")}
                  >
                    {customer?.email}
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Box className="text-16-regular" color={"#333333"}>
            <Box className="flex gap-2 mt-2 w-full justify-start">
              <WebsiteIcon />
              <NavLink className="break-all" to={customer?.website || ""} target="_blank">
                <Box sx={contactHover}>{customer?.website}</Box>
              </NavLink>
            </Box>
          </Box>
        </Box>
      </Box>

      <FullScreenImageDialog
        open={isFullScreenImage}
        onClose={() => setOpenFullScreenImage(false)}
        image={customer.logo?.content}
      />
    </>
  );
};

export default CustomerCard;
